import isEmpty from 'lodash-es/isEmpty';
import { isNullOrUndefined } from '@app/util/util';

import { ReservationType, UserHistoryItem } from '@user-history/entities/user-history-item';
import {
  getDefaultFlightReservations, isExpiredOrProcessing,
  getReservationExpiredOrProcessing, getPricesOverChanges
} from '@user-history/mappers/flight-reservations.mapper';
import { TRAVELEXTRA, isTravelExtraReservationInDetail } from '@user-history/mappers/user-history.mapper';
import {
  DetailReservation, HistoryBookingDetail, ReservationAssistance, ReservationFlight
} from '@user-history/entities/history-booking-detail';
import { ReservationStatus } from '@user-history/entities/reservation-status';
import {
  getCrossSellingAssistanceByDetail, getCrossSellingByProductItemByDetail
} from '@shared/cross-selling/mappers/cross-selling-by-detail.mapper';

export function initHistoryDetailVmMapper(
  rawDetail: HistoryBookingDetail,
  type: ReservationType,
  freeCancelationTimeRemaining: string
): HistoryBookingDetail {
  if (!isEmpty(rawDetail)) {
    const format = formatDetailReservations(rawDetail);
    switch (type) {
      case ReservationType.Flight:
        return {
          ...format,
          Reservations: getFlightReservationDetail(format).filter(item => !!item),
          FreeCancelationTimeRemaining: freeCancelationTimeRemaining,
          ExtraReservationRelationship: null,
          OtherExtras: [],
          hasExtra: isTravelExtraReservationInDetail(format)
        };
      case ReservationType.Extra:
        const defaultExtra = format.Reservations.find(res => res.ReservationType === ReservationType.Extra);
        return {
          ...format,
          FreeCancelationTimeRemaining: freeCancelationTimeRemaining,
          Reservations: [{
            ...defaultExtra,
          }].filter(item => !!item),
          ExtraReservationRelationship: extraRelationshipReservation(format),
          OtherExtras: format.Reservations
            .filter(_res => _res.ReservationType === ReservationType.Extra && _res.Id !== defaultExtra?.Id)
        };
      default:
        return {
          ...format,
          FreeCancelationTimeRemaining: freeCancelationTimeRemaining,
          Reservations: format.Reservations.filter(item =>
            item.ReservationType !== ReservationType.Extra &&
            item.ReservationType !== ReservationType.Assistance
          ),
          ExtraReservationRelationship: null,
          OtherExtras: [],
          hasExtra: isTravelExtraReservationInDetail(format)
        };
    }
  } else return rawDetail;
}

export function getHistoryDetailVmMapper(
  initDetailVm: HistoryBookingDetail,
  rawDetail: HistoryBookingDetail,
  type: ReservationType,
  history: UserHistoryItem[] = [],
): HistoryBookingDetail {
  const assistance = getAssistanceItem(rawDetail);
  return {
    ...initDetailVm,
    AssistanceReservation: assistance,
    CrossSellingByDetail: [
      getCrossSellingAssistanceByDetail(assistance, initDetailVm, type),
      getCrossSellingByProductItemByDetail(initDetailVm, history, type)
    ].filter(cross => !isEmpty(cross))
  };
}

function extraRelationshipReservation(format: HistoryBookingDetail): DetailReservation {
  const relationship = format.Reservations.filter(res =>
    res.ReservationType !== ReservationType.Extra &&
    res.ReservationType !== ReservationType.Assistance
  )?.[0];
  switch (relationship?.ReservationType) {
    case ReservationType.Flight:
      return getFlightReservationDetail(format)[0];
    default:
      return format.Reservations[0];
  }
}

function getAssistanceItem(rawDetail: HistoryBookingDetail): ReservationAssistance {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assistance = (rawDetail.Reservations as any[]).find(res => res.ReservationType === ReservationType.Assistance);
  return assistance ? assistance : null;
}

function onlyFlightReservation(detail: HistoryBookingDetail): ReservationFlight[] {
  return detail?.Reservations ?
    (detail.Reservations as ReservationFlight[]).filter(res => res.ReservationType === ReservationType.Flight) : [];
}

function getFlightReservationDetail(detail: HistoryBookingDetail): ReservationFlight[] {
  const reservations = onlyFlightReservation(detail) as ReservationFlight[];
  const _detail = isExpiredOrProcessing(detail.Status as ReservationStatus) ?
    getReservationExpiredOrProcessing(detail.Status as ReservationStatus, reservations) :
    reservations.length > 1 ? getFlightWithExchangeHistory(reservations) :
      reservations ? reservations : null;
  const _formatDetail = !!_detail &&
    _detail.map(reservation => isNullOrUndefined(reservation.History) ? { ...reservation, History: [] } : reservation);
  return _formatDetail;
}

function getFlightWithExchangeHistory(reservations: ReservationFlight[]): ReservationFlight[] {
  const priceChanges = getPricesOverChanges(reservations);
  const defaultReservations = getDefaultFlightReservations(reservations) as ReservationFlight[];
  return !isEmpty(defaultReservations) ?
    defaultReservations.map(_default => (
      {
        ..._default,
        History: historyFlightReservation(reservations, _default),
        PriceChanges: priceChanges ?? undefined
      }
    )) : null;
}

function historyFlightReservation(
  _filter: ReservationFlight[], _default: ReservationFlight, prev: ReservationFlight[] = []
): ReservationFlight[] {
  let history = prev;
  const exchanged = _filter.find(_item =>
    _item.Status === ReservationStatus.Exchanged &&
    _item.Id === _default.ExchangeAirReservationId &&
    _item.Id !== _default.Id
  );
  if (exchanged && _default.ExchangeAirReservationId) {
    history = [...history, exchanged];
    return historyFlightReservation(_filter, exchanged, history);
  } else return history;
}

function formatDetailReservations(rawDetail: HistoryBookingDetail): HistoryBookingDetail {
  return {
    ...rawDetail,
    Reservations: (rawDetail.Reservations as DetailReservation[])
      .filter(res => res.ReservationType !== ReservationType.Assistance)
      .map(reservation => {
        if (reservation.ReservationType as string === TRAVELEXTRA) {
          return { ...reservation, ReservationType: ReservationType.Extra };
        } else return reservation;
      })
  };
}

export const exportedForTesting = {
  getFlightWithExchangeHistory,
  extraRelationshipReservation
}
